/**
 * Deterministic, per-account activity-history dataset.
 *
 * Design goals:
 *  • Cover the five core activity categories Alpaca + Robinhood agree on:
 *    trades (fills), dividends, fees, transfers (ACH in/out), and
 *    interest accruals. Robinhood-specific categories (rewards, Gold
 *    billing, stock lending, event contracts, corporate actions) are
 *    out-of-scope — the "Core five" decision is tracked in MEMORY.md.
 *  • Seed a handful of pending dividends in the future so the
 *    Robinhood-style "Pending" section at the top of ActivitiesSimpleView
 *    has something to render.
 *  • Group by account so aggregate ("All Live") mode can fan out and
 *    the single-account view can scope cleanly. `activitiesForAccounts`
 *    mirrors `positionsForAccounts` for API parity.
 *  • Dates span ~5 weeks back + a couple of pending entries ahead of
 *    "today" so the month-group headers (March, April, pending) all
 *    have content.
 *  • All output is seeded + stable so demo screenshots don't drift.
 */

import { ACCOUNTS } from "./mock-data";

// ---- Types ---------------------------------------------------------------

/**
 * Five top-level activity categories. `trade` is further split into
 * buy/sell via `tradeSide`; transfers are split via `transferDirection`.
 */
export type ActivityType = "trade" | "dividend" | "fee" | "transfer" | "interest";

/** Settled = completed, Pending = scheduled/upcoming (e.g. ex-div dates). */
export type ActivityStatus = "settled" | "pending";

/** Trade side. Only set when `type === "trade"`. */
export type TradeSide = "buy" | "sell";

/** Transfer direction. Only set when `type === "transfer"`. */
export type TransferDirection = "in" | "out";

export interface Activity {
  id: string;
  accountId: string;
  /** ISO timestamp. Pending entries use a future date. */
  timestamp: string;
  type: ActivityType;
  status: ActivityStatus;
  /** Ticker symbol, when relevant (trades, dividends). */
  ticker?: string;
  /** Asset full name, for the Simple view's subtitle line. */
  assetName?: string;
  /** Signed dollar amount — positive = credit, negative = debit. */
  amount: number;
  /** Absolute share quantity, for trades + fractional dividends. */
  qty?: number | null;
  /** Per-share price at execution time, for trades. */
  price?: number | null;
  /** Sub-classifier for trades (buy/sell). */
  tradeSide?: TradeSide;
  /** Sub-classifier for transfers (in/out). */
  transferDirection?: TransferDirection;
  /** Free-form headline used by Simple view + Advanced description column. */
  description: string;
}

// ---- Ticker catalog ------------------------------------------------------

interface AssetEntry {
  ticker: string;
  name: string;
  price: number;
  /** Quarterly dividend per share; 0 for non-dividend payers. */
  divPerShare: number;
}

const ASSETS: AssetEntry[] = [
  { ticker: "AAPL",  name: "Apple Inc.",                price: 214.80, divPerShare: 0.25 },
  { ticker: "MSFT",  name: "Microsoft Corp.",           price: 428.12, divPerShare: 0.75 },
  { ticker: "NVDA",  name: "NVIDIA Corp.",              price: 118.42, divPerShare: 0.01 },
  { ticker: "GOOGL", name: "Alphabet Inc.",             price: 172.10, divPerShare: 0.20 },
  { ticker: "META",  name: "Meta Platforms, Inc.",      price: 582.30, divPerShare: 0.50 },
  { ticker: "AMZN",  name: "Amazon.com, Inc.",          price: 188.22, divPerShare: 0 },
  { ticker: "TSLA",  name: "Tesla, Inc.",               price: 245.60, divPerShare: 0 },
  { ticker: "AMD",   name: "Advanced Micro Devices",    price: 142.80, divPerShare: 0 },
  { ticker: "JPM",   name: "JPMorgan Chase & Co.",      price: 218.45, divPerShare: 1.25 },
  { ticker: "JNJ",   name: "Johnson & Johnson",         price: 160.30, divPerShare: 1.19 },
  { ticker: "SPY",   name: "SPDR S&P 500 ETF",          price: 545.20, divPerShare: 1.75 },
  { ticker: "QQQ",   name: "Invesco QQQ Trust",         price: 478.40, divPerShare: 0.62 },
  { ticker: "VDE",   name: "Vanguard Energy ETF",       price: 124.15, divPerShare: 0.92 },
  { ticker: "XOM",   name: "Exxon Mobil Corp.",         price: 118.90, divPerShare: 0.95 },
  { ticker: "ABBV",  name: "AbbVie Inc.",               price: 176.55, divPerShare: 1.55 },
  { ticker: "FAST",  name: "Fastenal Co.",              price: 46.80,  divPerShare: 0.22 },
  { ticker: "WM",    name: "Waste Management, Inc.",    price: 223.40, divPerShare: 0.75 },
  { ticker: "BLK",   name: "BlackRock, Inc.",           price: 1062.75, divPerShare: 5.10 },
  { ticker: "INTU",  name: "Intuit Inc.",               price: 645.20, divPerShare: 0.95 },
  { ticker: "FCX",   name: "Freeport-McMoRan, Inc.",    price: 42.30,  divPerShare: 0.15 }
];

const ASSET_BY_TICKER: Record<string, AssetEntry> = Object.fromEntries(
  ASSETS.map((a) => [a.ticker, a])
);

// ---- Seeded helpers ------------------------------------------------------

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * "Today" for seeding purposes. Kept as a fixed date so the generated
 * activity list doesn't drift between sessions; the Pending section
 * still lands in the future relative to this anchor.
 */
const ANCHOR_DATE = new Date("2026-04-17T20:00:00Z");

/** Offset-in-days applied to ANCHOR_DATE. Negative = past, positive = future. */
function dateFromOffset(daysOffset: number, hour: number, minute: number): string {
  const d = new Date(ANCHOR_DATE);
  d.setUTCDate(d.getUTCDate() + daysOffset);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ---- Per-account activity generation -------------------------------------

/**
 * Fixed trade templates per account — picked so each live account has
 * a plausible trading rhythm (momentum account churns tech; rotation
 * account cycles SPY/QQQ). Seed keeps the order stable.
 */
const TRADE_TEMPLATES: Record<string, Array<{ ticker: string; side: TradeSide; qty: number; dayOffset: number; hour: number }>> = {
  "acc-live-1": [
    { ticker: "SPY",  side: "buy",  qty: 3,  dayOffset: -1, hour: 13 },
    { ticker: "QQQ",  side: "sell", qty: 2,  dayOffset: -1, hour: 13 },
    { ticker: "WM",   side: "buy",  qty: 3,  dayOffset: 0,  hour: 16 },
    { ticker: "WM",   side: "buy",  qty: 1.476644124, dayOffset: 0, hour: 16 },
    { ticker: "FAST", side: "sell", qty: 22, dayOffset: 0,  hour: 16 },
    { ticker: "FAST", side: "sell", qty: 0.366817491, dayOffset: 0, hour: 16 },
    { ticker: "FAST", side: "buy",  qty: 21, dayOffset: -3, hour: 16 },
    { ticker: "FAST", side: "buy",  qty: 1.366817491, dayOffset: -3, hour: 16 },
    { ticker: "BLK",  side: "sell", qty: 0.968697811, dayOffset: -3, hour: 13 },
    { ticker: "VDE",  side: "buy",  qty: 1.614152,  dayOffset: 0, hour: 13 },
    { ticker: "XOM",  side: "buy",  qty: 8,  dayOffset: -8, hour: 14 },
    { ticker: "JNJ",  side: "sell", qty: 5,  dayOffset: -12, hour: 14 }
  ],
  "acc-live-2": [
    { ticker: "AAPL",  side: "buy",  qty: 10, dayOffset: -1, hour: 14 },
    { ticker: "NVDA",  side: "sell", qty: 6,  dayOffset: -2, hour: 13 },
    { ticker: "MSFT",  side: "buy",  qty: 4,  dayOffset: -4, hour: 13 },
    { ticker: "AMD",   side: "sell", qty: 15, dayOffset: -5, hour: 14 },
    { ticker: "META",  side: "buy",  qty: 2,  dayOffset: -9, hour: 14 },
    { ticker: "TSLA",  side: "sell", qty: 8,  dayOffset: -11, hour: 13 },
    { ticker: "GOOGL", side: "buy",  qty: 7,  dayOffset: -14, hour: 15 },
    { ticker: "AMZN",  side: "buy",  qty: 3,  dayOffset: -18, hour: 14 }
  ],
  "acc-1": [
    { ticker: "AAPL", side: "buy",  qty: 5, dayOffset: -2, hour: 13 },
    { ticker: "MSFT", side: "sell", qty: 2, dayOffset: -7, hour: 13 },
    { ticker: "NVDA", side: "buy",  qty: 8, dayOffset: -10, hour: 14 }
  ],
  "acc-2": [
    { ticker: "SPY",  side: "buy",  qty: 4, dayOffset: -3,  hour: 14 },
    { ticker: "QQQ",  side: "buy",  qty: 2, dayOffset: -6,  hour: 13 }
  ],
  "acc-3": [
    { ticker: "JNJ",  side: "buy",  qty: 6, dayOffset: -4,  hour: 13 },
    { ticker: "ABBV", side: "buy",  qty: 3, dayOffset: -8,  hour: 14 }
  ]
};

/** Which tickers pay dividends during the ~5-week window we're simulating. */
const DIVIDEND_EVENTS: Array<{
  accountId: string;
  ticker: string;
  dayOffset: number;
  /** null = settled, future date = pending. */
  pendingOn?: number;
  qty: number;
}> = [
  // Settled divs
  { accountId: "acc-live-1", ticker: "VDE",  dayOffset: -5,  qty: 18 },
  { accountId: "acc-live-1", ticker: "INTU", dayOffset: 0,   qty: 16.32 },
  { accountId: "acc-live-1", ticker: "SPY",  dayOffset: -8,  qty: 22 },
  { accountId: "acc-live-2", ticker: "AAPL", dayOffset: -10, qty: 62 },
  { accountId: "acc-live-2", ticker: "JPM",  dayOffset: -14, qty: 40 },
  { accountId: "acc-live-2", ticker: "MSFT", dayOffset: -17, qty: 26 },
  { accountId: "acc-1",      ticker: "JNJ",  dayOffset: -12, qty: 14 },
  { accountId: "acc-3",      ticker: "ABBV", dayOffset: -22, qty: 12 },
  // Pending (future) divs — these feed the "Pending" section header
  { accountId: "acc-live-1", ticker: "ABBV", dayOffset: 28, pendingOn: 28, qty: 0.12 },
  { accountId: "acc-live-1", ticker: "FCX",  dayOffset: 14, pendingOn: 14, qty: 0.2  },
  { accountId: "acc-live-2", ticker: "MSFT", dayOffset: 13, pendingOn: 13, qty: 0.015 },
  { accountId: "acc-live-2", ticker: "SPY",  dayOffset: 13, pendingOn: 13, qty: 0.154 },
  { accountId: "acc-live-2", ticker: "JPM",  dayOffset: 26, pendingOn: 26, qty: 40 }
];

/** Deterministic ACH transfers (deposits + a withdrawal) per account. */
const TRANSFER_EVENTS: Array<{
  accountId: string;
  direction: TransferDirection;
  amount: number;
  dayOffset: number;
}> = [
  { accountId: "acc-live-1", direction: "in",  amount: 2000, dayOffset: -6 },
  { accountId: "acc-live-1", direction: "out", amount: 500,  dayOffset: -20 },
  { accountId: "acc-live-2", direction: "in",  amount: 5000, dayOffset: -11 },
  { accountId: "acc-live-2", direction: "in",  amount: 1500, dayOffset: -24 },
  { accountId: "acc-1",      direction: "in",  amount: 1000, dayOffset: -15 },
  { accountId: "acc-2",      direction: "in",  amount: 750,  dayOffset: -19 },
  { accountId: "acc-3",      direction: "in",  amount: 1250, dayOffset: -21 }
];

/**
 * Build one account's activity list. Combines trade fills (with their
 * accompanying regulatory fees), dividend payments (settled + pending),
 * ACH transfers, and monthly interest accruals for cash swept into
 * high-yield.
 */
function buildActivitiesForAccount(accountId: string): Activity[] {
  const out: Activity[] = [];
  const rnd = mulberry32(hashStr(accountId));

  // Trades + per-trade regulatory fees. TAF/REG fees are tiny $0.01-ish
  // figures that Alpaca surfaces alongside sells — matching the
  // reference screenshot.
  for (const t of TRADE_TEMPLATES[accountId] ?? []) {
    const asset = ASSET_BY_TICKER[t.ticker];
    if (!asset) continue;

    // Add ±1% noise to the price so each fill looks independent.
    const priceJitter = 1 + (rnd() - 0.5) * 0.02;
    const price = asset.price * priceJitter;
    const amount = (t.side === "buy" ? -1 : 1) * t.qty * price;
    const ts = dateFromOffset(t.dayOffset, t.hour, Math.floor(rnd() * 60));
    const qtyDesc = t.qty % 1 === 0 ? t.qty.toString() : t.qty.toFixed(9);

    out.push({
      id: `${accountId}-fill-${t.ticker}-${t.dayOffset}-${t.side}-${qtyDesc}`,
      accountId,
      timestamp: ts,
      type: "trade",
      status: "settled",
      tradeSide: t.side,
      ticker: t.ticker,
      assetName: asset.name,
      qty: t.qty,
      price,
      amount,
      description: `${t.side === "buy" ? "Buy" : "Sell"} ${qtyDesc} ${t.ticker}`
    });

    // Sells trigger regulatory fees. Keep deterministic per trade.
    if (t.side === "sell") {
      const proceeds = t.qty * price;
      out.push({
        id: `${accountId}-fee-taf-${t.ticker}-${t.dayOffset}-${qtyDesc}`,
        accountId,
        timestamp: ts,
        type: "fee",
        status: "settled",
        amount: -Math.max(0.01, Math.round(t.qty * 166) / 1000000), // TAF ≈ $166/1M shares
        qty: null,
        description: `TAF fee for proceeds of ${qtyDesc} shares on ${ts.slice(0, 10)}`
      });
      out.push({
        id: `${accountId}-fee-reg-${t.ticker}-${t.dayOffset}-${qtyDesc}`,
        accountId,
        timestamp: ts,
        type: "fee",
        status: "settled",
        amount: -Math.max(0.01, Math.round(proceeds * 8) / 1000000), // SEC ≈ $8/$1M
        qty: null,
        description: `REG fee for proceeds of $${proceeds.toFixed(2)} on ${ts.slice(0, 10)}`
      });
    }
  }

  // Dividends — both settled and pending entries live in the same
  // stream so the filter bar's "Dividends" view shows them all.
  for (const d of DIVIDEND_EVENTS.filter((x) => x.accountId === accountId)) {
    const asset = ASSET_BY_TICKER[d.ticker];
    if (!asset) continue;
    const gross = asset.divPerShare * d.qty;
    const pending = d.pendingOn !== undefined;
    const ts = dateFromOffset(d.dayOffset, 9, 30);
    out.push({
      id: `${accountId}-div-${d.ticker}-${d.dayOffset}`,
      accountId,
      timestamp: ts,
      type: "dividend",
      status: pending ? "pending" : "settled",
      ticker: d.ticker,
      assetName: asset.name,
      qty: d.qty,
      price: asset.divPerShare,
      amount: Number(gross.toFixed(2)),
      description: pending ? `${d.ticker} dividend` : `Dividend from ${d.ticker}`
    });
  }

  // ACH transfers.
  for (const t of TRANSFER_EVENTS.filter((x) => x.accountId === accountId)) {
    const ts = dateFromOffset(t.dayOffset, 8, 0);
    out.push({
      id: `${accountId}-xfer-${t.direction}-${t.dayOffset}-${t.amount}`,
      accountId,
      timestamp: ts,
      type: "transfer",
      status: "settled",
      transferDirection: t.direction,
      amount: (t.direction === "in" ? 1 : -1) * t.amount,
      qty: null,
      description:
        t.direction === "in"
          ? `ACH deposit — $${t.amount.toLocaleString()}`
          : `ACH withdrawal — $${t.amount.toLocaleString()}`
    });
  }

  // Monthly interest on swept cash — tiny amounts, but real brokers
  // surface them and the reference Robinhood screen shows an Interest
  // payments filter, so we seed at least one per account.
  const interestAmt = Math.round((rnd() * 4 + 0.8) * 100) / 100;
  out.push({
    id: `${accountId}-int-march`,
    accountId,
    timestamp: dateFromOffset(-31, 9, 0),
    type: "interest",
    status: "settled",
    amount: interestAmt,
    qty: null,
    description: "Interest payment on swept cash (March)"
  });

  return out;
}

// ---- Public dataset ------------------------------------------------------

/**
 * Full activity stream across all accounts, sorted newest-first by
 * timestamp. Pending entries (future-dated) sort naturally above
 * settled ones because the Simple view groups them into their own
 * "Pending" section first.
 */
export const ACTIVITIES: Activity[] = ACCOUNTS.flatMap((a) =>
  buildActivitiesForAccount(a.id)
).sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));

/** Return the subset of activities that belong to the given accounts. */
export function activitiesForAccounts(accountIds: string[]): Activity[] {
  const set = new Set(accountIds);
  return ACTIVITIES.filter((a) => set.has(a.accountId));
}
